import { useState } from 'react';
import { BreakDetailDrawer } from '@/features/manager-breaks-demo/components/BreakDetailDrawer';
import { getEngineeringBreakById } from '@/features/manager-breaks-demo/data/breaks-demo-dataset';
import type { EngineeringBreak } from '@/features/manager-breaks-demo/types/breaks';
import {
  CommandCenterSidebar,
  SIDEBAR_WIDTH_CLASS,
} from '@/features/manager-command-center/components/CommandCenterSidebar';
import { DEFAULT_PROJECT, PROJECTS } from '../data/project-demo-dataset';
import { BreakingCommitmentsColumn } from './BreakingCommitmentsColumn';
import { ProceduresColumn } from './ProceduresColumn';
import { ProjectHero } from './ProjectHero';
import { ProjectSwitcher } from './ProjectSwitcher';
import { ProjectsOverviewGrid } from './ProjectsOverviewGrid';
import { ProjectTimeline } from './ProjectTimeline';
import { RemediationPlanPanel } from './RemediationPlanPanel';

/** Wide cap so the page uses the full width without becoming unreadable on ultrawide. */
const PAGE_WIDTH = 'max-w-[1640px]';

/** Shown in place of the remediation plan when a project is already on track. */
function HoldingThePlanBanner() {
  return (
    <div className="relative overflow-hidden rounded-3xl border border-emerald-100 bg-emerald-50/40 p-7 shadow-[0_30px_70px_-48px_rgba(16,185,129,0.4)]">
      <div className="flex items-center gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
          <svg
            viewBox="0 0 24 24"
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 13l4 4L19 7" />
          </svg>
        </span>
        <div>
          <h2 className="text-lg font-semibold text-emerald-900">
            No recovery needed — hold the plan.
          </h2>
          <p className="mt-1 text-sm text-emerald-700">
            Every signal is on pace. Keep the current cadence; Intera will flag
            the moment anything diverges.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * The project overview - the third manager surface, reached from the command
 * center. It answers one question about one project: is it going to hit its
 * deadline, and if not, what's the plan? Projects are switched in place; each
 * breaking commitment deep dives into the same examine view used elsewhere.
 */
export function ProjectOverviewPage() {
  // `null` shows the portfolio overview grid; a project id zooms into its
  // full deep-dive. The portfolio is the default landing read.
  const [openProjectId, setOpenProjectId] = useState<string | null>(null);
  const [selectedBreakId, setSelectedBreakId] = useState<string | null>(null);

  const project =
    PROJECTS.find((candidate) => candidate.id === openProjectId) ??
    DEFAULT_PROJECT;
  const isOnTrack = project.verdict === 'on-track';

  const selectedBreak: EngineeringBreak | null =
    (selectedBreakId && getEngineeringBreakById(selectedBreakId)) || null;

  // De-dupe the off-book column against "Breaking now": a rule already surfaced
  // as a live commitment is hidden here (counted in alsoLiveCount instead), so
  // the procedures column only carries the silent, not-yet-live violations.
  const liveBreakIds = new Set(
    project.breakingCommitments.map((commitment) => commitment.breakId)
  );
  const offBookProcedures = project.procedures.filter(
    (procedure) => !procedure.isFollowed
  );
  const silentOffBookProcedures = offBookProcedures.filter(
    (procedure) =>
      !(procedure.relatedBreakId && liveBreakIds.has(procedure.relatedBreakId))
  );
  const alsoLiveProcedureCount =
    offBookProcedures.length - silentOffBookProcedures.length;
  const followedProcedures = project.procedures.filter(
    (procedure) => procedure.isFollowed
  );

  return (
    <div className="relative min-h-[100dvh] bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_60%,#f3f6fb_100%)] text-black">
      {/* Faint alert glow bleeding from the top edge - ambient pressure. */}
      <div className="pointer-events-none absolute inset-x-0 -top-24 h-72 bg-[radial-gradient(58%_100%_at_50%_0%,rgba(244,63,94,0.08),transparent_72%)]" />

      <CommandCenterSidebar activeTab="project" />

      <div className={SIDEBAR_WIDTH_CLASS}>
        <main
          className={`relative z-10 mx-auto w-full px-5 pb-20 pt-8 md:px-10 ${PAGE_WIDTH}`}
        >
          {openProjectId === null ? (
            <ProjectsOverviewGrid
              projects={PROJECTS}
              onOpen={setOpenProjectId}
            />
          ) : (
            <>
              {/* Back to the portfolio + quick switch between projects. */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setOpenProjectId(null)}
                  className="flex shrink-0 items-center gap-1.5 rounded-full border border-zinc-200/70 px-3.5 py-2 text-sm font-medium text-zinc-500 transition-colors hover:bg-white hover:text-zinc-800"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4 rtl:rotate-180"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M19 12H5M11 18l-6-6 6-6" />
                  </svg>
                  All projects
                </button>
                <ProjectSwitcher
                  projects={PROJECTS}
                  activeProjectId={project.id}
                  onSelect={setOpenProjectId}
                />
              </div>

              {/* The bottom line - what to grasp first. */}
              <div className="mt-6">
                <ProjectHero project={project} />
              </div>

              {/* The schedule, with committed vs. projected. */}
              <div className="mt-8">
                <ProjectTimeline
                  timeline={project.timeline}
                  slipLabel={project.slipLabel}
                  isOnTrack={isOnTrack}
                />
              </div>

              {/* The plan to recover the deadline - or reassurance when on track. */}
              <div className="mt-8">
                {project.remediation ? (
                  <RemediationPlanPanel remediation={project.remediation} />
                ) : (
                  <HoldingThePlanBanner />
                )}
              </div>

              {/* Breaking commitments + procedures, fanned across the width. */}
              <section className="mt-8 grid gap-6 lg:grid-cols-3">
                <BreakingCommitmentsColumn
                  commitments={project.breakingCommitments}
                  onDeepDive={setSelectedBreakId}
                />
                <ProceduresColumn
                  procedures={silentOffBookProcedures}
                  variant="broken"
                  alsoLiveCount={alsoLiveProcedureCount}
                />
                <ProceduresColumn
                  procedures={followedProcedures}
                  variant="followed"
                />
              </section>
            </>
          )}
        </main>
      </div>

      <BreakDetailDrawer
        engineeringBreak={selectedBreak}
        onClose={() => setSelectedBreakId(null)}
      />
    </div>
  );
}
